import { on } from "../core/state.js";
import { registry } from "../core/registry.js";
import Header from "./base/Header.js";
import PromoPoster from "./base/PromoPoster.js";
import Banner from "./base/Banner.js";
import MessageList from "./base/MessageList.js";
import InputBar from "./base/InputBar.js";
import Footer from "./base/Footer.js";
import chatPanelCSS from "../styles/ChatPanel.css";
import * as reloadChatFeature from "../features/base/reload-chat.js";

const BASE_COMPONENTS = {
  Header,
  PromoPoster,
  Banner,
  MessageList,
  InputBar,
  Footer,
};

// Composer order: anchor strings render registered slots; named entries
// resolve via registry override or base default. Variants inject extras
// without touching this list.
const COMPOSER_LAYOUT = [
  "before-header",
  { name: "Header" },
  "after-header",
  "before-promo",
  { name: "PromoPoster" },
  "after-promo",
  "before-banner",
  { name: "Banner" },
  "after-banner",
  "before-messages",
  { name: "MessageList" },
  "after-messages",
  "before-input",
  { name: "InputBar" },
  "after-input",
  "before-footer",
  { name: "Footer" },
  "after-footer",
];

export class ChatPanel {
  constructor(shadow) {
    this.shadow = shadow;
    this._config = null;
    this._container = null;
    this._panel = null;
  }

  async init(config) {
    this._config = config;
    this._injectStyles();
    this._render();
    this._bindToggle();
  }

  _injectStyles() {
    const style = document.createElement("style");
    style.textContent = chatPanelCSS;
    this.shadow.appendChild(style);
  }

  _render() {
    this._container = document.createElement("div");
    this._container.id = "ws-chat-panel";

    this._panel = document.createElement("div");
    this._panel.className = "ws-panel";

    const ctx = {
      config: this._config,
      panelEl: this._panel,
      shadow: this.shadow,
    };

    let scrollContent = null;
    const scrollingTopEls = [];
    for (const entry of COMPOSER_LAYOUT) {
      if (typeof entry === "string") {
        this._renderSlot(entry, ctx);
      } else {
        const el = this._renderComponent(entry.name, ctx);
        if (entry.name === "MessageList" && el) {
          scrollContent = el.querySelector(".ws-messages");
        } else if ((entry.name === "PromoPoster" || entry.name === "Banner") && el) {
          scrollingTopEls.push(el);
        }
      }
    }

    // Parity: the promo poster + banner scroll WITH the conversation (old
    // layout placed them inside the scroll container, not pinned to the top).
    // Move them to the top of the message scroll content, keeping their order
    // (promo first, then banner). Falls back to the pinned position if the
    // message list has no scroll content (e.g. a variant override).
    if (scrollContent && scrollingTopEls.length) {
      const top = document.createElement("div");
      top.className = "ws-scroll-top";
      // promo first, then banner (COMPOSER_LAYOUT order)
      scrollingTopEls.forEach((el) => top.appendChild(el));
      scrollContent.prepend(top);
    }

    this._container.appendChild(this._panel);
    this.shadow.appendChild(this._container);

    // Header refresh → old reloadChat() (overlay + welcome re-seed), all tenants.
    reloadChatFeature.init({ panelEl: this._panel });
  }

  _renderComponent(name, ctx) {
    const Override = registry.getComponent(name);
    const Component = Override || BASE_COMPONENTS[name];
    if (!Component) return null;
    const el = new Component().render(ctx);
    if (el) this._panel.appendChild(el);
    return el;
  }

  _renderSlot(anchor, ctx) {
    const slotComponents = registry.getSlots(anchor);
    for (const Component of slotComponents) {
      const el = new Component().render(ctx);
      if (el) this._panel.appendChild(el);
    }
  }

  _bindToggle() {
    on("ws-toggle", ({ isOpen }) => {
      if (isOpen) {
        this._container.setAttribute("open", "");
      } else {
        this._container.removeAttribute("open");
      }
    });
  }
}
